import { addBreackLines, addMaxLength, capitalizeText } from '../../../../../util/formatters/stringNormalizer';
import { toEchartData } from '../dataProcessor';
import { calcularLabelFormatterComValoresMaximosMinimosEMedios } from './filteredLabelFormatter';
import formatter from './tooltipFormatter';

function sortLegends(legends, lines) {
  if (!lines || lines.length === 0) {
    return legends;
  }

  const sortedLegends = lines
    .map((line) => ({
      name: line.name,
      result: line.results[0],
    }))
    .sort((firstLine, secondLine) => secondLine.result.numeric - firstLine.result.numeric)
    .map((line) => line.name);

  return sortedLegends;
}

/**
 * divide uma array em duas arrays baseado
 * no predicate passado.
 *
 * ex:
 *
 * const numbers = [1,2,3,4,5];
 * const isEven = n => n % 2 === 0;
 * const [even, odd] = splitByPredicate(numbers, isEven);
 *
 *
 * @param {*} array
 * @param {*} predicate
 */
function splitByPredicate(array, predicate) {
  const positive = [];
  const negative = [];

  array.forEach((item) => {
    const result = predicate(item);
    if (result) {
      positive.push(item);
    } else {
      negative.push(item);
    }
  });

  return [positive, negative];
}

/**
 * insere um item uma determinada quantidade de vezes na array,
 * sendo inserido no início (beginning=true) ou no final da array. (beginning=false)
 * @param {*} item
 * @param {*} times
 * @param {*} array
 * @param {*} beginning
 */
function insert(item, times, array, beginning = true) {
  let position = 0;
  if (!beginning) {
    position = array.length;
  }

  for (let i = 0; i < times; i++) {
    array.splice(position, 0, item);
  }

  return array;
}

/**
 * insere uma quantidade x valores nulos em um determinado ponto da array passada.
 * @param {*} array array que terá os valores nulos inseridos
 * @param {*} numOfNullValues quantidade de valores nulos que serão inseridos
 * @param {*} beginning se deve ser inserido no início (true) ou no fim da array (false);
 */
function insertNullValues(array, numOfNullValues, beginning = true) {
  return insert(null, numOfNullValues, array, beginning);
}

/**
 * constrói uma série de valores não estimados.
 * (a linha não será pontilhada)
 *
 * @param {*} line dados da linha em si (nome da linha, por ex)
 * @param {*} results resultaods que devem ser plotados
 * @param {*} theme estilos que devem ser aplicados na série
 */
function buildNonPredictedSerie(line, results, theme, showLabel = true, chartFontSize = 12) {
  const { name } = line;

  return {
    name,
    data: results,
    type: 'line',
    lineStyle: {
      width: 5,
    },
    smooth: true,
    symbolSize: 6,
    label: {
      backgroundColor: theme.backgroundChartColor,
      padding: [0,6],
      position: "inside",
      show: showLabel,
      formatter: (params) => `${params.data.value}%`,
      textStyle: {
        color: theme.label.text,
        fontSize: chartFontSize,
      },
    },
  };
}

/**
 * constrói uma série de valores estimados.
 * (a linha será pontilhada)
 *
 * @param {*} line dados da linha em si (nome da linha, por ex)
 * @param {*} results resultaods que devem ser plotados
 * @param {*} theme estilos que devem ser aplicados na série
 */
function buildPredictedSerie(line, results, theme, showLabel = true, chartFontSize) {
  const { name } = line;

  return {
    name,
    data: results,
    type: 'line',
    lineStyle: {
      width: 4,
      type: 'dashed',
    },
    smooth: true,
    symbolSize: 6,
    label: {
      show: showLabel,
      backgroundColor: theme.backgroundChartColor,
      padding: [0,6],
      position: "inside",
      formatter: (params) => `${params.data.value}%`,
      textStyle: {
        color: theme.label.text,
        fontSize: chartFontSize,
      },
    },
  };
}

/**
 * converte uma linha em uma série. Os points são utilizados
 * como auxílio. O tema é utilizado para definir a parte visual
 * da série. ex: cor da linha, das labels, etc
 *
 * @param {*} line
 * @param {*} points
 * @param {*} tema
 */
function mapLineToSerie(line, points, theme, chartFontSize) {
  const [predicted, nonPredicted] = splitByPredicate(line.results, (item) => item.predicted);

  if (predicted.length > 0) {
    const lastNonPredicted = nonPredicted[nonPredicted.length - 1];
    lastNonPredicted.hideLabelFormatter = true;
    predicted.splice(0, 0, lastNonPredicted);
  }

  insertNullValues(nonPredicted, points.length - nonPredicted.length, false);
  insertNullValues(predicted, points.length - predicted.length, true);

  return [
    buildNonPredictedSerie(line, nonPredicted, theme, true, chartFontSize),
    buildPredictedSerie(line, predicted, theme, true, chartFontSize),
  ];
}

/**
 * constrói as séries do gráfico baseado nas linhas (lines) passadas,
 * os points são utilizados como auxílio. O tema é utilizado para definir a parte visual
 * das séries. ex: cor da linha, das labels, etc
 *
 * @param {*} lines
 * @param {*} points
 */
function mapLinesToSeries(lines, points, theme, chartFontSize) {
  let series = [];
  if (lines) {
    series = lines
      .map((line) => mapLineToSerie(line, points, theme, chartFontSize))
      .reduce((reduced, next) => ([...reduced, ...next]))
  }

  return series;
}

function getOptions(echartData, theme, chartFontSize = 14, disabledLegends = []) {
  const { legends, points, lines } = echartData;

  // Persistencia das legendas
  const selectedLegends = {};

  legends.forEach(({ name }) => selectedLegends[name] = !disabledLegends.includes(name));

  const sortedLegends = sortLegends(legends, lines);
  const series = mapLinesToSeries(lines, points, theme, chartFontSize);
  const seriesComValoresValidados = calcularLabelFormatterComValoresMaximosMinimosEMedios(series, points);

  return {
    visibleLegends: sortedLegends,
    color: theme.colors,
    tooltip: {
      trigger: 'axis',
      formatter,
      confine: true,
      axisPointer: {
        type: 'shadow',
      },
    },
    legend: {
      data: sortedLegends,
      selected: selectedLegends,
      type: 'scroll',
      textStyle: {
        color: theme.legend.color,
        fontSize: chartFontSize,
        fontFamily: 'Roboto, sans-serif'
      },
      formatter: capitalizeText,
      pageTextStyle: {
        color: theme.legend.pagination.text.color,
      },
      pageIconColor: theme.legend.pagination.buttons.color,
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      boundaryGap: true,
      data: addBreackLines(points, 20),
      show: true,
      axisTick: {
        alignWithLabel: true,
      },
      axisLabel: {
        formatter: (value) => addMaxLength(value, 20),
        interval: 0,
        rotate: 45,
        margin: 16,
        lineHeight: 15,
        textStyle: {
          color: theme.axis.x.label,
          fontSize: chartFontSize,
        },
      },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        textStyle: {
          color: theme.axis.y.label,
          fontSize: chartFontSize,
        },
      },
    },
    series: seriesComValoresValidados,
  };
}

export default function toEchartOption(resultadosEvolutivos, theme, fontSize, disabledLegends = []) {
  const echartData = toEchartData(resultadosEvolutivos);
  return getOptions(echartData, theme, fontSize, disabledLegends);
}
